/**
 * @fileoverview Provides Statsig integration using `StatsigProvider` to manage user context for feature flag evaluation. It uses `useAuth` to fetch user data and `createStatsigUser` to format the user for Statsig. The component initializes the Statsig SDK with environment-specific configurations.
 */

import React from 'react'
import { StatsigProvider as StatsigReactProvider, LogLevel } from '@statsig/react-bindings'
import { useAuth } from '../hooks/useAuth'
import type { User } from 'firebase/auth'
import { getEnv } from '../utils/env'

interface StatsigProviderProps {
  children: React.ReactNode
}

// Convert Firebase user to Statsig user format
const createStatsigUser = (firebaseUser: User | null) => {
  if (!firebaseUser) {
    return {
      userID: 'anonymous',
    }
  }

  return {
    userID: firebaseUser.uid,
    email: firebaseUser.email || undefined,
    custom: {
      displayName: firebaseUser.displayName || undefined,
      emailVerified: firebaseUser.emailVerified,
      // Add email domain for targeting rules
      emailDomain: firebaseUser.email?.split('@')[1] || undefined,
    },
  }
}

export const StatsigProvider: React.FC<StatsigProviderProps> = ({ children }) => {
  const { user, loading } = useAuth()

  // Don't render Statsig provider until auth is resolved
  if (loading) {
    return <>{children}</>
  }

  const statsigUser = createStatsigUser(user)
  const clientKey = import.meta.env.VITE_STATSIG_CLIENT_KEY

  // If no client key is configured, render without Statsig
  if (!clientKey) {
    console.warn('VITE_STATSIG_CLIENT_KEY not configured. Feature flags will be disabled.')
    return <>{children}</>
  }

  const tier = getEnv()

  return (
    <StatsigReactProvider
      sdkKey={clientKey}
      user={statsigUser}
      options={{
        // Enable debug mode in development
        logLevel: tier === 'development' || tier === 'staging' ? LogLevel.Debug : LogLevel.Error,
        environment: {
          tier,
        },
      }}
    >
      {children}
    </StatsigReactProvider>
  )
}
